import React, { useState, useContext } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";

import { ThemeProvider, alpha } from "@mui/material/styles";
import { crearTema } from "../theme/theme";

import { size } from "lodash";
import Loading from "../components/Loading";
import { DataContext } from "../context/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faEyeSlash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { simpleMessage } from "../helpers/Helpers";
import { apiLogin } from "../api/Account";
import fondo from "../components/media/fondo.jpg";

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {`Copyright © ${props.title} ${new Date().getFullYear()}.`}
    </Typography>
  );
}

// Pantalla dividida: a la izquierda el panel de marca (marino sobre la foto) y a la
// derecha el formulario sobre el fondo claro del theme. El Login ya no sigue el modo oscuro.
const tema = crearTema("light");
const iconoSecundario = tema.palette.text.secondary;
const { marino, marinoOscuro } = tema.palette.brand;

// La foto se adivina bajo un degradado de marca (del marino al marino oscuro).
const fondoMarca = `linear-gradient(160deg, ${alpha(marino, 0.9)} 0%, ${alpha(
  marinoOscuro,
  0.95
)} 100%), url(${fondo})`;

// Borde de los campos en text.secondary: el gris por defecto de MUI no llega a 3:1.
// El placeholder pasa a text.secondary sin atenuar (el 42 % de MUI da 2,6:1).
const estiloCampo = {
  "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
  "& input::placeholder": { color: "text.secondary", opacity: 1 },
};

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [pass, setPass] = useState("");

  const {
    setIsLoading,
    setIsDarkMode,
    setIsLogged,
    setAccess,
    title,
    slogan,
    icon,
    setIsServerOff,
  } = useContext(DataContext);

  const validar = (e) => {
    let isValid = true;
    if (!userName.trim()) {
      isValid = false;
      simpleMessage("Debe ingresar su usuario", "error");
      return;
    }

    if (!pass.trim()) {
      isValid = false;
      simpleMessage("Debe ingresar su contraseña", "error");
      return;
    }

    if (size(pass) < 6) {
      isValid = false;
      simpleMessage("la contraseña es menor de 6 caracteres", "error");
      return;
    }
    return isValid;
  };

  const iniciarSesion = async (event) => {
    event.preventDefault();
    if (!validar()) {
      return;
    }
    setIsLoading(true);
    const data = {
      userName: userName,
      password: pass,
    };
    const result = await apiLogin(data);
    if (!result.statusResponse) {
      simpleMessage(result.error, "error");
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsServerOff(result.data.userRol.isOff);
    setIsDarkMode(result.data.isDarkMode);
    setAccess(result.data.userRol.permissions);
    setIsLogged(true);
    setUserName("");
    setPass("");
  };

  return (
    <div style={{ "--color-foco": tema.palette.primary.main }}>
      <ThemeProvider theme={tema}>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            bgcolor: "background.default",
          }}
        >
          {/* Panel de marca: franja compacta en el teléfono, 55 % de la pantalla en escritorio. */}
          <Box
            component="header"
            sx={{
              flex: { xs: "0 0 auto", md: "0 0 55%" },
              display: "flex",
              alignItems: "center",
              px: { xs: 3, md: 8 },
              py: { xs: 2, md: 6 },
              color: "primary.contrastText",
              backgroundImage: fondoMarca,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <Stack
              direction={{ xs: "row", md: "column" }}
              spacing={{ xs: 2, md: 4 }}
              alignItems={{ xs: "center", md: "flex-start" }}
              sx={{ minWidth: 0, maxWidth: 520 }}
            >
              <Box
                component="img"
                alt="" /* decorativo: el nombre ya está en el h1 de al lado */
                src={icon}
                sx={{
                  width: { xs: 56, md: 128 },
                  height: { xs: 56, md: 128 },
                  flexShrink: 0,
                  borderRadius: "50%",
                  bgcolor: "background.paper",
                  objectFit: "contain",
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.5rem", md: "3rem" },
                    overflowWrap: "anywhere",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    color: "soft.primary",
                    fontSize: { xs: "0.9375rem", md: "1.375rem" },
                  }}
                >
                  {slogan}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Formulario sobre el fondo claro del theme. */}
          <Box
            component="main"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: { xs: "flex-start", md: "center" },
              alignItems: "center",
              px: 3,
              py: { xs: 4, md: 6 },
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 400 }}>
              <Typography
                component="h2"
                variant="h4"
                color="primary"
                sx={{ fontWeight: 700, mb: 4 }}
              >
                Iniciar Sesión
                {/* Punto celeste de la marca tras el título (decorativo). */}
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    color: "brand.celeste",
                    fontSize: "1.5em",
                    lineHeight: 0,
                  }}
                >
                  .
                </Box>
              </Typography>

              <Box
                component={"form"}
                onSubmit={iniciarSesion}
                sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
              >
                <TextField
                  fullWidth
                  onChange={(e) => setUserName(e.target.value)}
                  value={userName}
                  label="Usuario"
                  placeholder="Ingrese su usuario"
                  autoComplete="username"
                  sx={estiloCampo}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FontAwesomeIcon
                          icon={faUser}
                          style={{ color: iconoSecundario }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  onChange={(e) => setPass(e.target.value)}
                  value={pass}
                  label="Contraseña"
                  placeholder="Ingrese su contraseña"
                  autoComplete="current-password"
                  sx={estiloCampo}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"></InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={
                            showPassword
                              ? "Ocultar contraseña"
                              : "Mostrar contraseña"
                          }
                        >
                          {showPassword ? (
                            <FontAwesomeIcon
                              icon={faEye}
                              style={{ color: iconoSecundario }}
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faEyeSlash}
                              style={{ color: iconoSecundario }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  sx={{ mt: 1.5, py: 1.25 }}
                  color="primary"
                  fullWidth
                  type="submit"
                >
                  Iniciar Sesión
                </Button>

                {/* La pagina la sirve el servidor, no la SPA: por eso href y no navigate.
                    Es la primera instalacion; despues la app se actualiza sola. */}
                <Button
                  href="/descargar"
                  variant="text"
                  fullWidth
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                >
                  Descargar la app para Android
                </Button>
              </Box>

              <Copyright sx={{ mt: 5 }} title={title} />
            </Box>
          </Box>
        </Box>
      </ThemeProvider>

      <Loading />
    </div>
  );
};

export default Login;
